import React from "react";

interface Bundle {
  coins: number;
  price: number;
  value: number;
  type: string;
  savings?: number;
}

interface PricingProps {
  pricing: {
    regularCoins: number;
    regularLabel: string;
    specialCoins: number;
    specialLabel: string;
    whyCoins: string;
    bundles?: Bundle[];
  };
}

const PricingCoins: React.FC<PricingProps> = ({ pricing }) => (
  <section data-section="pricing" className="py-8 md:py-12">
    <div className="max-w-6xl mx-auto px-4">
      <h2 className="text-3xl font-bold text-white mb-6 text-center">💰 Coin Packages - Deals For Every Player!</h2>
      
      {/* Experience Pricing */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white/5 rounded-xl p-4 shadow-md text-center">
          <div className="text-lg font-semibold text-white mb-1">{pricing.regularLabel}</div>
          <div className="text-2xl font-bold text-blue-400 mb-1">{pricing.regularCoins} coins</div>
          <div className="text-xs text-white/70">per experience</div>
        </div>
        <div className="bg-white/5 rounded-xl p-4 shadow-md text-center">
          <div className="text-lg font-semibold text-white mb-1">{pricing.specialLabel}</div>
          <div className="text-2xl font-bold text-purple-400 mb-1">{pricing.specialCoins} coins</div>
          <div className="text-xs text-white/70">per experience</div>
        </div>
      </div>

      {/* Coin Bundles */}
      {pricing.bundles && (
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">🎁 Fantastic Bundles & Discounts</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {pricing.bundles.map((bundle, index) => (
              <div key={index} className={`rounded-xl p-4 text-center relative ${
                bundle.type === 'discount' 
                  ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/50' 
                  : 'bg-white/5'
              }`}>
                {bundle.type === 'discount' && bundle.savings && (
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold rounded-full px-2 py-1">
                    Save ${bundle.savings}
                  </div>
                )}
                <div className="text-2xl mb-1">
                  {bundle.coins === 1 ? '🪙' : bundle.coins <= 20 ? '🥉' : bundle.coins <= 100 ? '🥈' : '🥇'}
                </div>
                <div className="text-lg font-bold text-white">{bundle.coins} {bundle.type === 'coin' && bundle.coins === 1 ? 'Coin' : 'Coins'}</div>
                <div className="text-sm text-gray-300">${bundle.price.toFixed(2)}</div>
                {bundle.type === 'discount' && (
                  <div className="text-xs text-green-400 font-semibold mt-1">
                    Best Value!
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center text-white/80 mb-6">{pricing.whyCoins}</div>
      
      <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl p-6 border border-cyan-400/20 mb-6">
        <div className="text-center">
          <h3 className="text-xl font-bold text-white mb-2">🌟 Special Deals Available!</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
            <div>
              <div className="text-green-400 font-semibold">First Timers</div>
              <div>Welcoming deals</div>
            </div>
            <div>
              <div className="text-blue-400 font-semibold">Returned Customers</div>
              <div>Exclusive rewards</div>
            </div>
            <div>
              <div className="text-pink-400 font-semibold">Socializers</div>
              <div>Instant bonuses</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-4">
        <a href="/waiver" className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold shadow-lg hover:shadow-xl transition-all" data-cta="coins">Load Coins & Start Playing!</a>
      </div>
    </div>
  </section>
);

export default PricingCoins;
